import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { computePickerExclusions } from '@/features/projects/step-readiness'
import { cn } from '@/lib/utils'
import type { StepNode } from '@/api/types'

interface DependencyPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Every Step in the project's tree (any kind). Used to populate the multi-select list. */
  projectSteps: StepNode[]
  /** The Step whose dependencies are being edited. */
  stepId: string
  stepTitle: string
  /** Currently saved `dependsOn` for this Step. Used to pre-check rows + decide enable-save. */
  currentDependsOn: string[]
  pending: boolean
  onConfirm: (dependsOn: string[]) => void
}

/**
 * Phase 13 — multi-select dialog for editing one Step's `dependsOn`. Self / descendants / Steps
 * that already (transitively) depend on this Step are filtered out via {@link
 * computePickerExclusions} (frontend mirror of the backend cycle check). Saving sends the full
 * array to PATCH; the server re-validates as defense in depth.
 */
export const DependencyPicker = ({
  open,
  onOpenChange,
  projectSteps,
  stepId,
  stepTitle,
  currentDependsOn,
  pending,
  onConfirm,
}: DependencyPickerProps) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(currentDependsOn))
  const [search, setSearch] = useState('')

  // Reset local state on dialog open so a previous editing session's selection doesn't leak.
  useEffect(() => {
    if (open) {
      setSelected(new Set(currentDependsOn))
      setSearch('')
    }
  }, [open, currentDependsOn])

  // Pre-compute the exclusion set once per dialog open so we don't pay it per-render.
  const exclusions = useMemo(
    () => (open ? computePickerExclusions(projectSteps, stepId) : new Set<string>()),
    [open, projectSteps, stepId]
  )

  const flat = useMemo(() => {
    const rows: { step: StepNode; breadcrumb: string }[] = []
    const walk = (steps: StepNode[], breadcrumbs: string[]): void => {
      for (const step of steps) {
        rows.push({ step, breadcrumb: breadcrumbs.join(' › ') })
        walk(step.children, [...breadcrumbs, step.title])
      }
    }
    walk(projectSteps, [])
    return rows
  }, [projectSteps])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return flat
    return flat.filter(
      ({ step, breadcrumb }) =>
        step.title.toLowerCase().includes(q) || breadcrumb.toLowerCase().includes(q)
    )
  }, [flat, search])

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const dirty = useMemo(() => {
    if (selected.size !== currentDependsOn.length) return true
    for (const id of currentDependsOn) if (!selected.has(id)) return true
    return false
  }, [selected, currentDependsOn])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Set dependencies for &ldquo;{stepTitle}&rdquo;</DialogTitle>
          <DialogDescription>
            Pick the Steps that must be complete before this one is ready. Self, descendants, and
            anything that already depends on this Step are not selectable.
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Search steps..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
        />
        <div className="max-h-80 overflow-y-auto rounded-md border border-border">
          {filtered.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No matching steps.</p>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map(({ step, breadcrumb }) => {
                const excluded = exclusions.has(step.id)
                const checked = selected.has(step.id)
                return (
                  <li
                    key={step.id}
                    className={cn(
                      'flex items-start gap-3 p-3',
                      excluded && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    <Checkbox
                      id={`dep-${step.id}`}
                      checked={checked}
                      disabled={excluded || pending}
                      onCheckedChange={() => toggle(step.id)}
                      className="mt-1"
                    />
                    <label
                      htmlFor={`dep-${step.id}`}
                      className={cn('flex-1 text-sm', !excluded && 'cursor-pointer')}
                    >
                      <span className="font-medium">{step.title}</span>
                      <span className="ml-2 text-xs uppercase tracking-wide text-muted-foreground">
                        {step.kind === 'PROJECT_REFERENCE'
                          ? 'project ref'
                          : step.kind.toLowerCase()}
                      </span>
                      {breadcrumb && (
                        <div className="text-xs text-muted-foreground">{breadcrumb}</div>
                      )}
                      {excluded && (
                        <div className="text-xs text-muted-foreground">
                          {step.id === stepId
                            ? 'This is the Step you are editing'
                            : 'Cannot select — would create a cycle or is a descendant'}
                        </div>
                      )}
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(Array.from(selected))}
            disabled={!dirty || pending}
            type="button"
          >
            {pending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
